import React, { useEffect, useMemo, useState } from "react";
import { ListOrdered, Loader2 } from "lucide-react";
import { useQuery } from "@tanstack/react-query";

import {
  producaoService,
  RELATORIO_SABORES_PAGE_SIZE,
} from "../../../services/producaoService";
import RelatorioQueryErrorAlert from "../components/RelatorioQueryErrorAlert";
import RelatorioSaboresTablePagination from "../components/RelatorioSaboresTablePagination";
import { useRelatorioSabores } from "../context/RelatorioSaboresContext";
import { getErrorMessage } from "../../../utils/errorHandler";

function formatQuantidade(value: number): string {
  return value.toLocaleString("pt-BR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

const PizzasPorSaborTamanhoSaborTable: React.FC = () => {
  const { appliedFilters } = useRelatorioSabores();
  const [page, setPage] = useState(1);

  const filtersKey = useMemo(
    () => (appliedFilters ? JSON.stringify(appliedFilters) : ""),
    [appliedFilters]
  );

  useEffect(() => {
    setPage(1);
  }, [filtersKey]);

  const { data, isFetching, isError, error } = useQuery({
    queryKey: [
      "relatorio-sabores-agregado-paginado",
      filtersKey,
      page,
      RELATORIO_SABORES_PAGE_SIZE,
    ],
    queryFn: () =>
      producaoService.getRelatorioSaboresAgregadoLojaTamanhoSaborPaginado(
        appliedFilters!,
        page,
        RELATORIO_SABORES_PAGE_SIZE
      ),
    enabled: !!appliedFilters?.dataInicio && !!appliedFilters?.dataFim,
    placeholderData: (prev) => prev,
  });

  const linhas = data?.data ?? [];
  const totalPages = data?.totalPages ?? 1;
  const totalRegistros = data?.total ?? 0;
  const queryErrorMessage = isError ? getErrorMessage(error) : null;

  if (!appliedFilters?.dataInicio || !appliedFilters?.dataFim) {
    return null;
  }

  return (
    <section
      className="rounded-2xl border border-neutral-200/90 bg-white p-4 shadow-sm sm:p-6"
      aria-label="Quantidade total por loja, tamanho e sabor"
    >
      <div className="mb-4 flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <ListOrdered size={22} strokeWidth={2} />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="text-base font-semibold text-neutral-900">
            Totais por loja, tamanho e sabor
          </h2>
          <p className="mt-1 text-sm text-neutral-600">
            A quantidade considera a fragmentação da pizza: inteira = 1, meia =
            0,5, 1/3 = 0,33 e 1/4 = 0,25. Listagem paginada (
            {RELATORIO_SABORES_PAGE_SIZE} por página).
          </p>
        </div>
      </div>

      {queryErrorMessage ? (
        <RelatorioQueryErrorAlert message={queryErrorMessage} />
      ) : isFetching && data === undefined ? (
        <p className="flex items-center justify-center gap-2 py-8 text-sm text-neutral-500">
          <Loader2 className="h-5 w-5 animate-spin" />
          Carregando totais…
        </p>
      ) : (
        data != null && (
          <>
            <div className="overflow-x-auto rounded-xl border border-neutral-100">
              <table className="w-full min-w-[560px] text-left text-sm">
                <thead>
                  <tr className="border-b border-neutral-200 bg-neutral-50/90">
                    <th className="px-3 py-2.5 font-semibold text-neutral-800 sm:px-4">
                      Loja
                    </th>
                    <th className="px-3 py-2.5 font-semibold text-neutral-800 sm:px-4">
                      Tamanho
                    </th>
                    <th className="px-3 py-2.5 font-semibold text-neutral-800 sm:px-4">
                      Sabor
                    </th>
                    <th className="px-3 py-2.5 text-right font-semibold text-neutral-800 sm:px-4">
                      Quantidade
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {linhas.length === 0 ? (
                    <tr>
                      <td
                        colSpan={4}
                        className="px-4 py-8 text-center text-sm text-neutral-500"
                      >
                        Nenhuma produção encontrada para os filtros aplicados.
                      </td>
                    </tr>
                  ) : (
                    linhas.map((row, i) => (
                      <tr
                        key={`${row.lojaNome}-${row.tamanhoNome}-${row.saborNome}-${i}`}
                        className="border-b border-neutral-100 last:border-b-0"
                      >
                        <td className="px-3 py-2.5 text-neutral-900 sm:px-4">
                          {row.lojaNome}
                        </td>
                        <td className="px-3 py-2.5 text-neutral-900 sm:px-4">
                          {row.tamanhoNome}
                        </td>
                        <td className="px-3 py-2.5 text-neutral-900 sm:px-4">
                          {row.saborNome}
                        </td>
                        <td className="px-3 py-2.5 text-right font-medium tabular-nums text-neutral-900 sm:px-4">
                          {formatQuantidade(row.quantidade)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
                <tfoot className="border-t-2 border-neutral-200 bg-neutral-50/90">
                  <tr>
                    <td
                      colSpan={3}
                      className="px-3 py-2.5 text-sm font-semibold text-neutral-800 sm:px-4"
                    >
                      Totais
                    </td>
                    <td className="px-3 py-2.5 text-right text-sm text-neutral-800 sm:px-4">
                      <div className="tabular-nums">
                        <div>
                          Geral:{" "}
                          <span className="font-bold text-neutral-900">
                            {formatQuantidade(
                              data.totalQuantidadeFracionadaGeral
                            )}
                          </span>
                        </div>
                        <div className="mt-0.5 text-xs font-normal">
                          Nesta página:{" "}
                          <span className="font-semibold text-neutral-900">
                            {formatQuantidade(
                              data.totalQuantidadeFracionadaPagina
                            )}
                          </span>
                        </div>
                      </div>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
            <RelatorioSaboresTablePagination
              page={data.page}
              totalPages={totalPages}
              totalRegistros={totalRegistros}
              totalGeralFmt={formatQuantidade(
                data.totalQuantidadeFracionadaGeral
              )}
              totalPaginaFmt={formatQuantidade(
                data.totalQuantidadeFracionadaPagina
              )}
              isLoading={isFetching}
              onPrevious={() => setPage((p) => Math.max(1, p - 1))}
              onNext={() => setPage((p) => Math.min(totalPages, p + 1))}
            />
          </>
        )
      )}
    </section>
  );
};

export default PizzasPorSaborTamanhoSaborTable;
